/**
 * @fileoverview Componente de detalhes da promoção com itens (produtos, categorias, marcas).
 * Interação idêntica ao padrão Category.jsx / Brand.jsx:
 * - Produtos: exibe ProductCard com modal de detalhes ao clicar
 * - Categorias: dispatch(selectCategory) → Workspace renderiza ProductCategory com botão voltar
 * - Marcas: dispatch(selectBrand) → Workspace renderiza ProductBrand com botão voltar
 *
 * @module PromotionDetails
 * @since 1.0.0
 */

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { apiRequestJson } from '../../api/apiService';
import {
  clearSelectedPromotion,
  selectCategory,
  selectBrand,
} from '../../store/slices/productsSlice';
import { setPromotionContext } from '../../store/slices/promotionSlice';
import ProductCard from '../Product/ProductCard';
import CategoryCard from '../Category/CategoryCard';
import BrandCard from '../Brand/BrandCard';

/**
 * Normaliza array de targets para array de IDs strings,
 * independente de ser [{id, name}] ou ["id1", "id2"].
 * @param {Array} arr
 * @returns {Array<string>}
 */
const toIds = (arr = []) =>
  arr.map((item) => (typeof item === 'object' ? String(item.id || item._id) : String(item)));

/**
 * Componente de detalhes da promoção.
 * @component
 * @returns {JSX.Element}
 */
export default function PromotionDetails() {
  const dispatch = useDispatch();
  const { selectedPromotion } = useSelector((state) => state.products);
  const [items, setItems] = useState({ products: [], categories: [], brands: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!selectedPromotion) return;

      setLoading(true);

      const targets = selectedPromotion.targets || {};
      const productIds = toIds(targets.products);
      const categoryIds = toIds(targets.categories);
      const brandIds = toIds(targets.brands);

      const loaded = { products: [], categories: [], brands: [] };

      try {
        // Busca todos os produtos ativos uma única vez
        const prodRes = await apiRequestJson('products_list');
        const activeProducts = (prodRes.success && prodRes.data)
          ? prodRes.data.filter(
              (p) => p.ativo !== false && p.ativo !== 0 && p.removido !== true && p.removido !== 1
            )
          : [];

        // filter: produtos diretos da promoção
        if (productIds.length > 0) {
          loaded.products = activeProducts.filter((p) => productIds.includes(String(p.id)));
        }

        // filter: categorias da promoção
        if (categoryIds.length > 0) {
          const catRes = await apiRequestJson('products_categories');
          if (catRes.success && catRes.data) {
            loaded.categories = catRes.data.filter((c) => categoryIds.includes(String(c.id)));
          }
        }

        // filter: marcas da promoção
        if (brandIds.length > 0) {
          const brandRes = await apiRequestJson('products_brands');
          if (brandRes.success && brandRes.data) {
            loaded.brands = brandRes.data.filter((b) => brandIds.includes(String(b.id)));
          }
        }
      } catch (err) {
        console.error('Erro ao carregar itens da promoção:', err);
      } finally {
        setItems(loaded);
        setLoading(false);
      }
    };

    load();
  }, [selectedPromotion]);

  /**
   * Clique em CategoryCard: salva contexto da promoção e seleciona categoria.
   * Workspace detecta selectedCategory e renderiza ProductCategory.
   * ProductCategory usa promotionContext para voltar à promoção ao clicar em "Voltar".
   * @param {Object} category
   */
  const handleCategoryClick = (category) => {
    dispatch(setPromotionContext(selectedPromotion));
    dispatch(selectCategory(category));
  };

  /**
   * Clique em BrandCard: salva contexto da promoção e seleciona marca.
   * Workspace detecta selectedBrand e renderiza ProductBrand.
   * ProductBrand usa promotionContext para voltar à promoção ao clicar em "Voltar".
   * @param {Object} brand
   */
  const handleBrandClick = (brand) => {
    dispatch(setPromotionContext(selectedPromotion));
    dispatch(selectBrand(brand));
  };

  if (!selectedPromotion) return null;

  if (loading) {
    return (
      <Box
        component="main"
        sx={{ pt: 8, pb: 6, minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const hasItems =
    items.products.length > 0 || items.categories.length > 0 || items.brands.length > 0;

  return (
    <Box component="main" sx={{ pt: 8, pb: 6, minHeight: '100vh' }}>
      <Container
        maxWidth='lg'
        sx={{
          py: 4,
          mt: { xs: 5, sm: 5, md: '100px' },
          px: { xs: 1, sm: 3, md: 3 },
        }}
      >
        {/* Cabeçalho sticky com título e botão ← — padrão de ProductCategory/ProductBrand */}
        <Box
          sx={{
            position: 'sticky',
            top: { xs: 64, md: 80 },
            zIndex: 100,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: { xs: 8, sm: 8, md: 8 },
          }}
        >
          <Typography variant='h5' component='h5' sx={{ textAlign: 'left', fontWeight: 'bold' }}>
            {selectedPromotion.name}
          </Typography>
          <IconButton
            onClick={() => dispatch(clearSelectedPromotion())}
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              boxShadow: 2,
              '&:hover': { backgroundColor: 'primary.dark' },
            }}
            aria-label="Voltar"
          >
            <ArrowBack />
          </IconButton>
        </Box>

        {selectedPromotion.context?.description && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {selectedPromotion.context.description}
          </Typography>
        )}

        {!hasItems && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Nenhum item encontrado nesta promoção.
          </Alert>
        )}

        {/* Produtos diretos da promoção */}
        {items.products.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 'bold' }}>
              Produtos
            </Typography>
            <Grid
              container
              spacing={{ xs: 1, sm: 3, md: 3 }}
              sx={{ maxWidth: { xs: '100%', sm: 1000, md: 1200 }, width: '100%', margin: 0 }}
            >
              {/* map: renderiza ProductCard para cada produto direto da promoção */}
              {items.products.map((product) => (
                <Grid
                  key={product.id}
                  size={{ xs: 6, sm: 6, md: 3, lg: 3 }}
                  sx={{ display: 'flex', justifyContent: 'center', minWidth: 0, maxWidth: '100%' }}
                >
                  <ProductCard
                    id={product.id}
                    nome={product.nome}
                    marca={product.marca}
                    preco={product.preco}
                    imagem={product.imagem}
                    categoria={product.categoria}
                    descricao={product.descricao}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* Categorias da promoção — clique navega para ProductCategory via Workspace */}
        {items.categories.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 'bold' }}>
              Categorias
            </Typography>
            <Grid
              container
              spacing={{ xs: 1, sm: 3, md: 3 }}
              sx={{ maxWidth: { xs: '100%', sm: 1000, md: 1200 }, width: '100%', margin: 0 }}
            >
              {/* map: renderiza CategoryCard; clique usa dispatch(selectCategory) como em Category.jsx */}
              {items.categories.map((category) => (
                <Grid
                  key={category.id}
                  size={{ xs: 6, sm: 6, md: 3, lg: 3 }}
                  sx={{ display: 'flex', justifyContent: 'center', minWidth: 0, maxWidth: '100%' }}
                >
                  <CategoryCard
                    id={category.id}
                    name={category.name}
                    parent_id={category.parent_id}
                    enable={category.enable}
                    image_url={category.image_url}
                    onClick={handleCategoryClick}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* Marcas da promoção — clique navega para ProductBrand via Workspace */}
        {items.brands.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 'bold' }}>
              Marcas
            </Typography>
            <Grid
              container
              spacing={{ xs: 1, sm: 3, md: 3 }}
              sx={{ maxWidth: { xs: '100%', sm: 1000, md: 1200 }, width: '100%', margin: 0 }}
            >
              {/* map: renderiza BrandCard; clique usa dispatch(selectBrand) como em Brand.jsx */}
              {items.brands.map((brand) => (
                <Grid
                  key={brand.id}
                  size={{ xs: 6, sm: 6, md: 3, lg: 3 }}
                  sx={{ display: 'flex', justifyContent: 'center', minWidth: 0, maxWidth: '100%' }}
                >
                  <BrandCard
                    id={brand.id}
                    name={brand.name}
                    enable={brand.enable}
                    image_url={brand.image_url}
                    onClick={handleBrandClick}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {/* Botão Voltar no final do grid — padrão de ProductCategory/ProductBrand */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={() => dispatch(clearSelectedPromotion())}
            sx={{ px: 4, py: 1.5 }}
          >
            Voltar
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
